import { DataType, Form, FormOptions, Section, useNotify } from '@globaleris/ui.library'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { EnumCommodity, EnumPrecioUnitario } from '../../../../../api'
import { PriceCoefficientTable } from '../../../../../api/Argentia/endpoints/priceCoefficients/model/PriceCoefficient'
import { EnumRedondeo } from '../../../../../api/Argentia/enums'
import { EnumPriceType } from '../../../../../api/oData/enums/enumPriceType'
import { PriceCoefficientsCard } from '../../../../../components/shared/priceCoefficientsCard/priceCoefficientsCard'
import SimpleDialog from '../../../../../components/shared/simpleDialog/simpleDialog'
import {
  enumEnumToValueNumber,
  enumToSelectOptionArray,
  enumToSelectOptionArrayDefaultValues
} from '../../../../../helpers'
import { missingFieldsFormWithRequiredText } from '../../../../../helpers/validateFormHelper'
import { getCustomerDomain } from '../../../../../redux'
import { createPriceComponentV2 } from '../../../../priceComponents/priceComponents.service'
import { PriceComponentFormData } from '../../components/priceComponentTypeForm'
import {
  mapDataToPriceComponentFormData,
  mapPriceComponentFormDataToRequest,
  mapPricingTableModelToPriceComponentTypeForm
} from '../../../pricing.mapper'
import { PricingTableModel } from '../../../pricing.Model'

interface AddDefaultValueDialogProps {
  open: boolean
  onClose: (refreshNeeded?: boolean) => void
  pricingTableRowSelected: PricingTableModel
  commodity: EnumCommodity
}

export const AddDefaultValueDialog: React.FC<AddDefaultValueDialogProps> = ({
  open,
  onClose,
  pricingTableRowSelected,
  commodity
}) => {
  const [t] = useTranslation([
    'globalerisLibrary',
    'priceComponentType',
    'enums',
    'sharedTranslations'
  ])
  const { info, error } = useNotify()

  const priceType =
    enumEnumToValueNumber(EnumPriceType, pricingTableRowSelected.priceType) ??
    EnumPriceType.Parametro

  const showPeriodValues = priceType === EnumPriceType.ParametroPeriodo
  const showCoefficients = priceType === EnumPriceType.Apuntamiento

  const getInitialFormData = (): PriceComponentFormData | undefined => {
    const typeFormData = mapPricingTableModelToPriceComponentTypeForm(pricingTableRowSelected)
    if (!typeFormData) return undefined
    return mapDataToPriceComponentFormData(pricingTableRowSelected.idPriceComponentType!, {
      ...typeFormData,
      commodity
    })
  }

  const [formData, setFormData] = useState<PriceComponentFormData | undefined>(getInitialFormData)
  const [priceCoefficientsData, setCoefficientsData] = useState<PriceCoefficientTable[]>([])

  const defaultPriceTypeOptions: EnumPriceType[] = [
    EnumPriceType.Parametro,
    EnumPriceType.ParametroPeriodo,
    EnumPriceType.Apuntamiento
  ]

  const formSections: Section[] = useMemo(() => {
    const sections: Section[] = [
      {
        title: t('priceComponentType:form.priceComponentFormAdd.sectionTitle1'),
        inputs: [
          {
            name: 'priceComponentTypeCode',
            label: t('priceComponentType:form.add.inputs.code'),
            type: DataType.TEXT,
            readOnly: true
          },
          {
            name: 'priceComponentTypeDesc',
            label: t('priceComponentType:form.add.inputs.description'),
            type: DataType.TEXT,
            readOnly: true
          },
          {
            name: 'priceType',
            label: t('priceComponentType:form.add.inputs.priceType'),
            selectOptions: enumToSelectOptionArrayDefaultValues(
              EnumPriceType,
              'enums:EnumPriceTypes',
              defaultPriceTypeOptions
            ),
            required: true,
            type: DataType.SELECT,
            readOnly: true
          },
          {
            name: 'commodity',
            label: t('priceComponentType:form.add.inputs.commodity'),
            selectOptions: enumToSelectOptionArray(EnumCommodity, 'enums:EnumCommodity'),
            type: DataType.SELECT,
            required: true,
            readOnly: true
          }
        ]
      },
      {
        title: t('priceComponentType:form.priceComponentFormAdd.sectionTitle2'),
        inputs: [
          {
            name: 'code',
            label: t('priceComponentType:form.add.inputs.code'),
            type: DataType.TEXT,
            maxLength: 56,
            required: true,
            readOnly: true
          },
          {
            name: 'description',
            label: t('priceComponentType:form.add.inputs.description'),
            type: DataType.TEXT,
            maxLength: 200,
            readOnly: true
          },
          {
            name: 'unitPrice',
            label: t('priceComponentType:form.add.inputs.unitPrice'),
            type: DataType.SELECT,
            selectOptions: enumToSelectOptionArray(EnumPrecioUnitario, 'enums:CurrencyAndUnit'),
            required: true,
            readOnly: true
          },
          {
            name: 'rounding',
            label: t('priceComponentType:form.add.inputs.rounding'),
            type: DataType.SELECT,
            selectOptions: enumToSelectOptionArray(EnumRedondeo, 'enums:EnumRedondeo'),
            readOnly: true
          }
        ]
      }
    ]

    if (!showCoefficients) {
      sections.push({
        title: t('priceComponentType:form.priceComponentFormAdd.sectionTitle3'),
        inputs: [
          {
            name: 'startDate',
            label: t('priceComponentType:form.priceComponentFormAdd.inputs.startDate'),
            type: DataType.DATE,
            required: true
          },
          {
            name: 'value',
            label: t('priceComponentType:form.priceComponentFormAdd.inputs.value'),
            type: DataType.NUMBER,
            display: !showPeriodValues,
            customDecimalScale: formData?.rounding,
            clearable: true
          },
          {
            name: 'period1Value',
            label: t('priceComponentType:form.priceComponentFormAdd.inputs.period1Value'),
            type: DataType.NUMBER,
            display: showPeriodValues,
            customDecimalScale: formData?.rounding,
            clearable: true
          },
          {
            name: 'period2Value',
            label: t('priceComponentType:form.priceComponentFormAdd.inputs.period2Value'),
            type: DataType.NUMBER,
            display: showPeriodValues,
            customDecimalScale: formData?.rounding,
            clearable: true
          },
          {
            name: 'period3Value',
            label: t('priceComponentType:form.priceComponentFormAdd.inputs.period3Value'),
            type: DataType.NUMBER,
            display: showPeriodValues,
            customDecimalScale: formData?.rounding,
            clearable: true
          },
          {
            name: 'period4Value',
            label: t('priceComponentType:form.priceComponentFormAdd.inputs.period4Value'),
            type: DataType.NUMBER,
            display: showPeriodValues,
            customDecimalScale: formData?.rounding,
            clearable: true
          },
          {
            name: 'period5Value',
            label: t('priceComponentType:form.priceComponentFormAdd.inputs.period5Value'),
            type: DataType.NUMBER,
            display: showPeriodValues,
            customDecimalScale: formData?.rounding,
            clearable: true
          },
          {
            name: 'period6Value',
            label: t('priceComponentType:form.priceComponentFormAdd.inputs.period6Value'),
            type: DataType.NUMBER,
            display: showPeriodValues,
            customDecimalScale: formData?.rounding,
            clearable: true
          }
        ]
      })
    }

    return sections
  }, [formData?.rounding, showCoefficients, showPeriodValues]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async (data: PriceComponentFormData): Promise<boolean> => {
    if (data) {
      if (missingFieldsFormWithRequiredText(formSections, data)) {
        error(t('priceComponentType:notifications.create.errorInvalidForm'))
        return false
      }
      const request = mapPriceComponentFormDataToRequest(data, priceCoefficientsData)
      const response = await createPriceComponentV2(request)
      if (response.success) {
        info(t('priceComponentType:notifications.create.successPriceComponent'))
        onClose(true)
        return true
      }
      error(t('priceComponentType:notifications.create.errorPriceComponent'))
      return false
    }
    // Form cancel button pressed
    onClose(false)
    return false
  }

  const formOptions: FormOptions = {
    elevation: 0,
    columns: 4,
    analytics: {
      customer: getCustomerDomain(),
      view: t('priceComponentType:form.priceComponentFormAdd.analytics')
    },
    onSave: (data: any) => handleSave(data)
  }

  return (
    <SimpleDialog open={open} handleClose={() => onClose(false)} formStyle="formContent" hideFooter>
      <Form sections={formSections} data={{ ...formData }} options={formOptions} />
      {showCoefficients && (
        <PriceCoefficientsCard
          rounding={formData?.rounding ?? 6}
          onTableDataChange={setCoefficientsData}
          style={{ margin: '0 0 24px 0' }}
          raised={false}
        />
      )}
    </SimpleDialog>
  )
}
